import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../constants';
import { useToast } from '../components/ui/Toast';

type ApiStatus = 'checking' | 'connected' | 'error' | 'waking';

interface ApiContextType {
  apiStatus: ApiStatus;
  checkApiHealth: () => Promise<void>;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [apiStatus, setApiStatus] = useState<ApiStatus>('checking');
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 20; // 20 attempts * 3 seconds = 60 seconds (typical Render cold start)
  const { addToast } = useToast();

  const checkApiHealth = useCallback(async () => {
    try {
      // We use a timeout signal to fail fast if it's hanging, forcing a retry loop
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const res = await fetch(`${API_BASE_URL}/`, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (res.ok) {
        setApiStatus('connected');
        retryCountRef.current = 0;
      } else {
        throw new Error(`Status: ${res.status}`);
      }
    } catch (err) {
      console.warn("API Check failed, retrying...", err);
      
      if (retryCountRef.current < MAX_RETRIES) {
        setApiStatus('waking');
        retryCountRef.current += 1;
        setTimeout(checkApiHealth, 3000); // Retry every 3 seconds
      } else {
        setApiStatus('error');
      }
    }
  }, []);

  useEffect(() => {
    // 1. Initial Health Check
    checkApiHealth();

    // 2. Keep-Alive Heartbeat (Ping every 10 mins to prevent sleeping while tab is open)
    const heartbeatInterval = setInterval(() => {
        if (document.visibilityState === 'visible') {
            fetch(`${API_BASE_URL}/`).catch(() => {}); // Silent fire-and-forget
        }
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(heartbeatInterval);
  }, [checkApiHealth]);

  return (
    <ApiContext.Provider value={{ apiStatus, checkApiHealth }}>
      {children}
    </ApiContext.Provider>
  );
};

export const useApi = () => {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};
